/**
 * Facility entity geometry helpers — orientation, Flip and canonical
 * parking size.
 *
 * Orientation is DERIVED from width vs height; there is no orientation /
 * rotation column. Flip is a geometry rotation — width and height swap at
 * the same x/y anchor — so the rendered rectangle changes shape while its
 * content (label, icon, badges, ticket info) stays upright and reflows. No
 * CSS rotate transform is involved anywhere.
 */
import type { FacilityEntity, FacilityEntityType } from '@/types/facility';
import { BAY_HEIGHT, BAY_WIDTH, PARKING_HEIGHT, PARKING_WIDTH } from '@/components/facility/constants';

export type EntityOrientation = 'horizontal' | 'vertical' | 'square';

export function deriveOrientation(width: number, height: number): EntityOrientation {
  if (width > height) return 'horizontal';
  if (height > width) return 'vertical';
  return 'square';
}

/** Physical footprints where orientation is meaningful. Zones and doors are excluded. */
const FLIPPABLE_TYPES = new Set<FacilityEntityType>([
  'lift_2post', 'lift_4post', 'lift_scissor', 'lift_single_post',
  'alignment', 'adas', 'alignment_adas', 'wash', 'inspection', 'flat',
  'parking_space',
]);

export function isFlippable(entity: Pick<FacilityEntity, 'entityType'>): boolean {
  return FLIPPABLE_TYPES.has(entity.entityType);
}

/** W×H → H×W. Only the size changes; callers keep x/y, id and source as-is. */
export function flipDimensions(entity: Pick<FacilityEntity, 'width' | 'height'>): { width: number; height: number } {
  return { width: entity.height, height: entity.width };
}

/**
 * Entity types that use the canonical standard service-bay footprint
 * (BAY_WIDTH×BAY_HEIGHT). Specialty envelopes (alignment / ADAS) are
 * excluded — see ENTITY_RENDER_CONFIG.
 */
export const STANDARD_BAY_TYPES: ReadonlySet<FacilityEntityType> = new Set<FacilityEntityType>([
  'lift_2post', 'lift_4post', 'lift_scissor', 'lift_single_post', 'wash', 'inspection', 'flat',
]);

/** Canonical vertical footprint for types that have one, else null. */
function canonicalVerticalFootprint(entityType: FacilityEntityType): { width: number; height: number } | null {
  if (entityType === 'parking_space') return { width: PARKING_WIDTH, height: PARKING_HEIGHT };
  if (STANDARD_BAY_TYPES.has(entityType)) return { width: BAY_WIDTH, height: BAY_HEIGHT };
  return null;
}

/**
 * "Apply Default Size": the canonical footprint in the entity's CURRENT
 * orientation — horizontal keeps horizontal (e.g. parking 360×180, bay
 * 480×280); vertical or square → canonical vertical (180×360 / 280×480).
 * Returns null for types without a canonical footprint (specialty bays,
 * zones, doors). Only width/height are returned.
 */
export function canonicalDefaultSize(
  entity: Pick<FacilityEntity, 'entityType' | 'width' | 'height'>,
): { width: number; height: number } | null {
  const vertical = canonicalVerticalFootprint(entity.entityType);
  if (!vertical) return null;
  return deriveOrientation(entity.width, entity.height) === 'horizontal'
    ? { width: vertical.height, height: vertical.width }
    : vertical;
}
